import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { PhotoSlot } from "@shyne/shared/api";
import { AppointmentWorkflow } from "@/models/appointment-task.model";
import { AppointmentClockData } from "@/models/appointment.model";
import { OnPhotoUpload } from "./PhotoUploadSection";
import { optimizeImage, formatFileSize } from "@/lib/image-optimizer";
import { AppointmentPhotoRepository } from "@/repositories/appointment-photos.repository";
import { ImageLightbox } from "@/components/shared/image-lightbox";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { getDistinctWorkflowDescription } from "@/lib/utils/workflow-photo-gate";
import {
  Camera,
  Upload,
  CheckCircle2,
  Loader2,
  X,
  Trash2,
  Plus,
  RefreshCw,
  Expand,
} from "lucide-react";

// ─── Types ──────────────────────────────────────────────────

type WorkflowStep =
  | { type: "clock"; key: string; label: string; description: string }
  | {
      type: "workflow";
      key: string;
      label: string;
      description: string | null;
      workflow: PhotoSlot;
      isAfter: boolean;
    };

/** Captured/selected file for a step, not yet uploaded */
interface CapturedFile {
  file: File;
  previewUrl: string;
}

interface BulkUploadFile {
  id: string;
  file: File;
  previewUrl: string;
  assignedStepKey: string | null;
}

type Phase = "camera" | "preview" | "assign" | "confirm";

// Flow ceremonies, in job order (mode names keep the legacy clock terms —
// the UI wording is Start Work / Complete Work):
//   clockin   — start-work front-door photo, then the guided before tour
//   aftertour — the guided after tour only (no clock photo, no status change)
//   clockout  — the completion front-door photo only, submitted with the
//               Complete Work commit
export type GuidedWorkflowMode = "clockin" | "aftertour" | "clockout";

interface GuidedPhotoWorkflowProps {
  open: boolean;
  onClose: () => void;
  mode: GuidedWorkflowMode;
  appointmentId: string;
  serviceWorkflows: PhotoSlot[];
  appointmentWorkflows: AppointmentWorkflow[];
  clockInData?: AppointmentClockData;
  clockOutData?: AppointmentClockData;
  onPhotoUpload: OnPhotoUpload;
  onComplete: (clockPhotoUrl: string) => void | Promise<void>;
}

// ─── Stepper Dots ───────────────────────────────────────────

function StepperDots({
  steps,
  capturedFiles,
  alreadyDone,
  currentIndex,
  phase,
}: {
  steps: WorkflowStep[];
  capturedFiles: Map<string, CapturedFile>;
  alreadyDone: Set<string>;
  currentIndex: number;
  phase: Phase;
}) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {steps.map((step, i) => {
        const isDone = alreadyDone.has(step.key);
        const isCaptured = capturedFiles.has(step.key);
        const isCurrent =
          (phase === "camera" || phase === "preview") && i === currentIndex;

        return (
          <div
            key={step.key}
            className={cn(
              "rounded-full transition-all duration-300",
              (isDone || isCaptured) && "w-3 h-3 bg-green-500",
              !isDone &&
                !isCaptured &&
                isCurrent &&
                "w-3 h-3 border-2 border-white bg-transparent",
              !isDone &&
                !isCaptured &&
                !isCurrent &&
                "w-2.5 h-2.5 bg-white/30"
            )}
          />
        );
      })}
    </div>
  );
}

// ─── Confirmation List (shared by confirm phase) ────────────

function ConfirmationList({
  steps,
  capturedFiles,
  alreadyDone,
  appointmentWorkflows,
  onRetake,
  onReplace,
  submitting,
}: {
  steps: WorkflowStep[];
  capturedFiles: Map<string, CapturedFile>;
  alreadyDone: Set<string>;
  appointmentWorkflows: AppointmentWorkflow[];
  onRetake: (stepIndex: number) => void;
  onReplace: (stepKey: string) => void;
  submitting: boolean;
}) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [lightboxAlt, setLightboxAlt] = useState("");

  return (
    <>
      <div className="space-y-2">
        {steps.map((step, i) => {
          const captured = capturedFiles.get(step.key);
          const wasAlreadyDone = alreadyDone.has(step.key);
          const hasPhoto = !!captured || wasAlreadyDone;

          // Get thumbnail
          let thumbSrc: string | undefined;
          if (captured) {
            thumbSrc = captured.previewUrl;
          } else if (wasAlreadyDone && step.type === "workflow") {
            const existing = appointmentWorkflows.find(
              (aw) =>
                aw.workflow === step.workflow.id && aw.is_after === step.isAfter
            );
            thumbSrc = existing?.image;
          }

          return (
            <div
              key={step.key}
              className="flex items-center gap-3 p-3 rounded-lg border"
            >
              {hasPhoto ? (
                <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{step.label}</p>
                {step.description && (
                  <p className="text-xs text-muted-foreground truncate">
                    {step.description}
                  </p>
                )}
              </div>
              {thumbSrc && (
                <button
                  onClick={() => {
                    setLightboxUrl(thumbSrc!);
                    setLightboxAlt(step.label);
                  }}
                  className="shrink-0 relative rounded-lg overflow-hidden active:scale-95 transition-transform ring-1 ring-border"
                >
                  <img
                    src={thumbSrc}
                    alt={step.label}
                    className="w-14 h-14 object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <Expand className="w-5 h-5 text-white drop-shadow" />
                  </div>
                </button>
              )}
              {/* Retake / Replace buttons */}
              {!submitting && (
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => onRetake(i)}
                    className="min-w-[36px] min-h-[36px] flex items-center justify-center rounded-md hover:bg-muted transition-colors"
                    title="Retake with camera"
                  >
                    <RefreshCw className="w-4 h-4 text-muted-foreground" />
                  </button>
                  <button
                    onClick={() => onReplace(step.key)}
                    className="min-w-[36px] min-h-[36px] flex items-center justify-center rounded-md hover:bg-muted transition-colors"
                    title="Upload replacement"
                  >
                    <Upload className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <ImageLightbox
        open={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        imageUrl={lightboxUrl || ""}
        alt={lightboxAlt}
      />
    </>
  );
}

// ─── Main Component ─────────────────────────────────────────

export function GuidedPhotoWorkflow({
  open,
  onClose,
  mode,
  appointmentId,
  serviceWorkflows,
  appointmentWorkflows,
  clockInData,
  clockOutData,
  onPhotoUpload,
  onComplete,
}: GuidedPhotoWorkflowProps) {
  // Phase & navigation
  const [phase, setPhase] = useState<Phase>("camera");
  const [currentIndex, setCurrentIndex] = useState(0);

  // Captured files (not yet uploaded) — key is step.key
  const [capturedFiles, setCapturedFiles] = useState<Map<string, CapturedFile>>(
    new Map()
  );
  // Steps that were already uploaded before this flow opened
  const [alreadyDone, setAlreadyDone] = useState<Set<string>>(new Set());

  // Preview hold state (2 second display after capture)
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Camera
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Bulk upload (assign phase)
  const [bulkFiles, setBulkFiles] = useState<BulkUploadFile[]>([]);

  // Close confirmation
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Hidden file inputs
  const bulkInputRef = useRef<HTMLInputElement | null>(null);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);
  const replaceStepKeyRef = useRef<string | null>(null);

  // Steps ref for callbacks
  const stepsRef = useRef<WorkflowStep[]>([]);

  // ─── Build step list ──────────────────────────────────────

  const steps: WorkflowStep[] = [];
  if (mode === "clockin") {
    steps.push({
      type: "clock",
      key: "clockin-photo",
      label: "Start Work Photo",
      description: "Take a picture of the front door to confirm your arrival",
    });
    serviceWorkflows.forEach((wf) => {
      steps.push({
        type: "workflow",
        key: `before-${wf.id}`,
        label: wf.title,
        description: getDistinctWorkflowDescription(wf),
        workflow: wf,
        isAfter: false,
      });
    });
  } else if (mode === "aftertour") {
    serviceWorkflows.forEach((wf) => {
      steps.push({
        type: "workflow",
        key: `after-${wf.id}`,
        label: wf.title,
        description: getDistinctWorkflowDescription(wf),
        workflow: wf,
        isAfter: true,
      });
    });
  } else {
    steps.push({
      type: "clock",
      key: "clockout-photo",
      label: "Complete Work Photo",
      description: "Take a picture of the front door to confirm your departure",
    });
  }
  stepsRef.current = steps;

  // ─── Camera lifecycle ─────────────────────────────────────

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    if (streamRef.current) return;
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
    } catch {
      setCameraError(
        "Unable to access camera. Grant camera permissions or upload photos instead."
      );
    }
  }, []);

  // Re-attach stream when switching back to camera phase
  useEffect(() => {
    if (phase === "camera" && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [phase]);

  // ─── Initialize on open ───────────────────────────────────

  useEffect(() => {
    if (!open) {
      stopCamera();
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
      return;
    }

    // Compute already-done steps (previously uploaded)
    const done = new Set<string>();
    if (mode === "clockin" && clockInData?.photoUrl) {
      done.add("clockin-photo");
    }
    if (mode === "clockout" && clockOutData?.photoUrl) {
      done.add("clockout-photo");
    }
    if (mode !== "clockout") {
      const isAfter = mode === "aftertour";
      serviceWorkflows.forEach((wf) => {
        const existing = appointmentWorkflows.find(
          (aw) => aw.workflow === wf.id && aw.is_after === isAfter
        );
        if (existing) {
          done.add(`${isAfter ? "after" : "before"}-${wf.id}`);
        }
      });
    }
    setAlreadyDone(done);
    setCapturedFiles(new Map());
    setCameraError(null);
    setBulkFiles([]);
    setSubmitting(false);
    setUploadProgress(0);
    setPreviewImage(null);

    // If all already done, go straight to confirm
    const currentSteps = stepsRef.current;
    if (currentSteps.length > 0 && currentSteps.every((s) => done.has(s.key))) {
      setPhase("confirm");
      setCurrentIndex(0);
      return;
    }

    // Find first incomplete step
    const firstIncomplete = currentSteps.findIndex((s) => !done.has(s.key));
    setCurrentIndex(firstIncomplete >= 0 ? firstIncomplete : 0);
    setPhase("camera");
    startCamera();
  }, [open]);

  // ─── Close ────────────────────────────────────────────────

  const forceClose = useCallback(() => {
    stopCamera();
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    capturedFiles.forEach((cf) => URL.revokeObjectURL(cf.previewUrl));
    bulkFiles.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    setShowCloseConfirm(false);
    onClose();
  }, [onClose, capturedFiles, bulkFiles, stopCamera]);

  const handleClose = useCallback(() => {
    const hasIncomplete = stepsRef.current.some(
      (s) => !capturedFiles.has(s.key) && !alreadyDone.has(s.key)
    );
    // Show dialog if incomplete steps OR unsaved captures exist
    if (hasIncomplete || capturedFiles.size > 0) {
      setShowCloseConfirm(true);
    } else {
      forceClose();
    }
  }, [capturedFiles, alreadyDone, forceClose]);

  // ─── Escape key ───────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, handleClose]);

  // ─── Helpers ──────────────────────────────────────────────

  const isStepReady = useCallback(
    (step: WorkflowStep) =>
      capturedFiles.has(step.key) || alreadyDone.has(step.key),
    [capturedFiles, alreadyDone]
  );

  const allStepsReady = steps.every(isStepReady);

  /** Find the next step that needs a photo */
  const findNextIncomplete = useCallback(
    (afterIndex: number): number | null => {
      const currentSteps = stepsRef.current;
      for (let i = afterIndex + 1; i < currentSteps.length; i++) {
        const s = currentSteps[i];
        if (!capturedFiles.has(s.key) && !alreadyDone.has(s.key)) return i;
      }
      for (let i = 0; i <= afterIndex; i++) {
        const s = currentSteps[i];
        if (!capturedFiles.has(s.key) && !alreadyDone.has(s.key)) return i;
      }
      return null;
    },
    [capturedFiles, alreadyDone]
  );

  // ─── Capture from camera ──────────────────────────────────

  const captureFromCamera = useCallback(() => {
    if (!videoRef.current) return;
    const step = stepsRef.current[currentIndex];
    if (!step) return;

    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);

    // Full-size preview data URL for the 2-second hold
    const fullPreviewUrl = canvas.toDataURL("image/jpeg", 0.7);

    // Create file from blob (stored locally, uploaded later on submit)
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `capture-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });

        // Store the captured file + preview
        setCapturedFiles((prev) => {
          const next = new Map(prev);
          // Revoke old preview if retaking
          const old = prev.get(step.key);
          if (old) URL.revokeObjectURL(old.previewUrl);
          next.set(step.key, { file, previewUrl: fullPreviewUrl });
          return next;
        });

        // Show 2-second preview hold
        setPreviewImage(fullPreviewUrl);
        setPhase("preview");

        if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
        previewTimerRef.current = setTimeout(() => {
          setPreviewImage(null);
          // Check if all steps are now captured
          const currentSteps = stepsRef.current;
          const allReady = currentSteps.every(
            (s) =>
              s.key === step.key ||
              capturedFiles.has(s.key) ||
              alreadyDone.has(s.key)
          );

          if (allReady) {
            stopCamera();
            setPhase("confirm");
          } else {
            // Advance to next incomplete step
            const nextIdx = findNextIncomplete(currentIndex);
            if (nextIdx !== null) {
              setCurrentIndex(nextIdx);
            }
            setPhase("camera");
          }
        }, 1000);
      },
      "image/jpeg",
      0.9
    );
  }, [currentIndex, capturedFiles, alreadyDone, findNextIncomplete, stopCamera]);

  // ─── Bulk upload handlers (assign phase) ──────────────────

  const handleBulkFilesSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const newEntries: BulkUploadFile[] = Array.from(files)
        .filter((f) => f.type.startsWith("image/"))
        .map((f) => ({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file: f,
          previewUrl: URL.createObjectURL(f),
          assignedStepKey: null,
        }));

      if (newEntries.length === 0) {
        toast.error("No valid image files selected");
        e.target.value = "";
        return;
      }

      if (phase === "camera" || phase === "preview") {
        stopCamera();
        if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
        setPreviewImage(null);
        setPhase("assign");
      }

      setBulkFiles((prev) => [...prev, ...newEntries]);
      e.target.value = "";
    },
    [phase, stopCamera]
  );

  const removeBulkFile = (id: string) => {
    setBulkFiles((prev) => {
      const entry = prev.find((f) => f.id === id);
      if (entry) URL.revokeObjectURL(entry.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  };

  const assignBulkFile = (id: string, stepKey: string) => {
    setBulkFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, assignedStepKey: stepKey } : f))
    );
  };

  const incompleteSteps = steps.filter(
    (s) => !capturedFiles.has(s.key) && !alreadyDone.has(s.key)
  );

  const allBulkAssigned =
    bulkFiles.length > 0 &&
    incompleteSteps.every((step) =>
      bulkFiles.some((f) => f.assignedStepKey === step.key)
    );

  /** Move bulk-assigned files into capturedFiles, then go to confirm or camera */
  const commitBulkAssignment = () => {
    setCapturedFiles((prev) => {
      const next = new Map(prev);
      for (const bf of bulkFiles) {
        if (bf.assignedStepKey) {
          // Revoke old preview if overwriting
          const old = prev.get(bf.assignedStepKey);
          if (old) URL.revokeObjectURL(old.previewUrl);
          next.set(bf.assignedStepKey, {
            file: bf.file,
            previewUrl: bf.previewUrl,
          });
        } else {
          // Unassigned file — clean up
          URL.revokeObjectURL(bf.previewUrl);
        }
      }
      return next;
    });
    setBulkFiles([]);

    // Check if all steps are covered now
    const currentSteps = stepsRef.current;
    const allReady = currentSteps.every((s) => {
      if (alreadyDone.has(s.key) || capturedFiles.has(s.key)) return true;
      // Check if a bulk file was assigned to this step
      return bulkFiles.some((bf) => bf.assignedStepKey === s.key);
    });

    if (allReady) {
      stopCamera();
      setPhase("confirm");
    } else {
      // Go back to camera for remaining
      const firstIdx = currentSteps.findIndex(
        (s) =>
          !alreadyDone.has(s.key) &&
          !capturedFiles.has(s.key) &&
          !bulkFiles.some((bf) => bf.assignedStepKey === s.key)
      );
      setCurrentIndex(firstIdx >= 0 ? firstIdx : 0);
      setPhase("camera");
      startCamera();
    }
  };

  // ─── Confirmation: retake / replace ───────────────────────

  const handleRetake = useCallback(
    (stepIndex: number) => {
      setCurrentIndex(stepIndex);
      setPhase("camera");
      startCamera();
    },
    [startCamera]
  );

  const handleReplace = useCallback((stepKey: string) => {
    replaceStepKeyRef.current = stepKey;
    replaceInputRef.current?.click();
  }, []);

  const handleReplaceFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      const stepKey = replaceStepKeyRef.current;
      if (!file || !stepKey || !file.type.startsWith("image/")) {
        e.target.value = "";
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setCapturedFiles((prev) => {
        const next = new Map(prev);
        const old = prev.get(stepKey);
        if (old) URL.revokeObjectURL(old.previewUrl);
        next.set(stepKey, { file, previewUrl });
        return next;
      });

      e.target.value = "";
      replaceStepKeyRef.current = null;
    },
    []
  );

  // ─── Submit: upload all captured files ────────────────────

  const handleSubmit = async () => {
    setSubmitting(true);
    setUploadProgress(0);
    let latestClockUrl: string | null = null;

    // Only upload steps that have a captured file (skip alreadyDone)
    const toUpload = steps.filter((s) => capturedFiles.has(s.key));
    let uploaded = 0;

    try {
      for (const step of toUpload) {
        const captured = capturedFiles.get(step.key);
        if (!captured) continue;

        const originalSize = formatFileSize(captured.file.size);
        const optimized = await optimizeImage(captured.file);
        const optimizedSize = formatFileSize(optimized.size);
        if (optimized.size < captured.file.size * 0.8) {
          toast.info(
            `${step.label}: ${originalSize} → ${optimizedSize}`
          );
        }

        if (step.type === "clock") {
          latestClockUrl = await AppointmentPhotoRepository.upload(appointmentId, mode === "clockin" ? "clockIn" : "clockOut", optimized);
        } else {
          await Promise.resolve(
            onPhotoUpload(step.workflow.id, optimized, step.isAfter)
          );
        }

        uploaded++;
        setUploadProgress(Math.round((uploaded / toUpload.length) * 100));
      }

      // Clean up preview URLs
      capturedFiles.forEach((cf) => URL.revokeObjectURL(cf.previewUrl));

      // Complete. The after tour has no clock step — its completion handler
      // only closes the flow, so the URL is empty there.
      stopCamera();
      const clockUrl =
        latestClockUrl ||
        (mode === "clockin"
          ? clockInData?.photoUrl
          : mode === "clockout"
            ? clockOutData?.photoUrl
            : "") ||
        "";
      // No success toast here: the completion handler owns the outcome ("Work
      // started/completed" or the checklist-gate error). Toasting "photos
      // uploaded" first showed a success immediately followed by an error
      // whenever the clock-out was rejected. Awaiting also keeps the flow in
      // its submitting state so a slow completion can't be double-submitted.
      await Promise.resolve(onComplete(clockUrl));
    } catch (error) {
      // A rejected completion (checklist gate, stale status) already surfaced
      // its own toast in the handler; upload-step failures are toasted here.
      // Either way the flow stays open so it never looks like work finished.
      console.error("Guided workflow submit failed:", error);
      if (!(uploaded === toUpload.length)) {
        toast.error("Upload failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Cleanup on unmount ───────────────────────────────────

  useEffect(() => {
    return () => {
      stopCamera();
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    };
  }, [stopCamera]);

  // ─── Early return ─────────────────────────────────────────

  if (!open || steps.length === 0) return null;

  const currentStep = steps[currentIndex];
  const headerTitle =
    phase === "camera" || phase === "preview"
      ? currentStep?.label || ""
      : phase === "assign"
        ? "Assign Photos"
        : "Review Photos";

  // ─── Render via portal ────────────────────────────────────

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black">
      <div
        className={cn(
          "h-[100dvh] w-full flex flex-col",
          "sm:items-center sm:justify-center sm:bg-black/80 sm:p-4",
          "sm:h-[100dvh]"
        )}
      >
        <div
          className={cn(
            "flex flex-col h-full w-full bg-background",
            "sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:w-full sm:rounded-xl sm:border sm:shadow-2xl sm:overflow-hidden"
          )}
        >
          {/* ── Header ─────────────────────────────────── */}
          <header className="shrink-0 px-4 py-3 flex flex-col gap-2 border-b bg-background z-10">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-semibold truncate">
                  {headerTitle}
                </h2>
                {(phase === "camera" || phase === "preview") && currentStep?.description && (
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                    {currentStep.description}
                  </p>
                )}
              </div>
              <button
                onClick={handleClose}
                className="min-w-[48px] min-h-[48px] flex items-center justify-center rounded-full bg-muted hover:bg-muted/80 transition-colors shrink-0"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <StepperDots
              steps={steps}
              capturedFiles={capturedFiles}
              alreadyDone={alreadyDone}
              currentIndex={currentIndex}
              phase={phase}
            />
          </header>

          {/* ── Camera Phase ───────────────────────────── */}
          {phase === "camera" && (
            <>
              <main className="flex-1 min-h-0 relative bg-black flex items-center justify-center">
                {cameraError ? (
                  <div className="text-white text-center p-6 space-y-4">
                    <Camera className="w-12 h-12 mx-auto opacity-50" />
                    <p className="text-sm">{cameraError}</p>
                    <Button
                      variant="outline"
                      className="text-white border-white/30"
                      onClick={() => bulkInputRef.current?.click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload from gallery
                    </Button>
                  </div>
                ) : (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                )}
              </main>
              <footer className="shrink-0 px-4 py-4 flex items-center justify-center gap-6 bg-black/60 backdrop-blur-sm border-t border-white/10">
                <button
                  onClick={captureFromCamera}
                  disabled={!!cameraError}
                  className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-lg active:scale-95 transition-transform disabled:opacity-40"
                >
                  <div className="w-[56px] h-[56px] rounded-full border-2 border-black/10" />
                </button>
                <button
                  onClick={() => bulkInputRef.current?.click()}
                  className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center active:scale-95 transition-transform"
                >
                  <Upload className="w-5 h-5 text-white" />
                </button>
              </footer>
            </>
          )}

          {/* ── Preview Phase (2-second hold) ──────────── */}
          {phase === "preview" && previewImage && (
            <>
              <main className="flex-1 min-h-0 relative bg-black flex items-center justify-center">
                <img
                  src={previewImage}
                  alt="Captured"
                  className="w-full h-full object-contain"
                />
                <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                  <div className="bg-black/60 backdrop-blur-sm text-white text-sm px-4 py-2 rounded-full flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    Photo captured
                  </div>
                </div>
              </main>
              <footer className="shrink-0 px-4 py-4 bg-black/60 backdrop-blur-sm border-t border-white/10" />
            </>
          )}

          {/* ── Assign Phase (bulk upload) ─────────────── */}
          {phase === "assign" && (
            <>
              <main className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
                <Button
                  variant="outline"
                  className="w-full border-dashed border-2"
                  onClick={() => bulkInputRef.current?.click()}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add more photos
                </Button>

                {bulkFiles.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-start gap-3 p-2 border rounded-lg"
                  >
                    <img
                      src={entry.previewUrl}
                      alt="Preview"
                      className="w-14 h-14 object-cover rounded shrink-0"
                    />
                    <div className="flex-1 min-w-0 space-y-1">
                      <p className="text-xs text-muted-foreground truncate">
                        {entry.file.name}
                      </p>
                      <Select
                        value={entry.assignedStepKey || ""}
                        onValueChange={(val) => assignBulkFile(entry.id, val)}
                      >
                        <SelectTrigger className="w-full h-8 text-xs">
                          <SelectValue placeholder="Assign to step..." />
                        </SelectTrigger>
                        <SelectContent>
                          {incompleteSteps.map((step) => {
                            const alreadyAssigned = bulkFiles.some(
                              (f) =>
                                f.id !== entry.id &&
                                f.assignedStepKey === step.key
                            );
                            return (
                              <SelectItem
                                key={step.key}
                                value={step.key}
                                disabled={alreadyAssigned}
                              >
                                {step.label}
                                {alreadyAssigned ? " (assigned)" : ""}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <button
                      onClick={() => removeBulkFile(entry.id)}
                      className="shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </button>
                  </div>
                ))}

                {bulkFiles.length > 0 && !allBulkAssigned && (
                  <div className="text-xs text-muted-foreground mt-2">
                    <p className="font-medium mb-1">Still need photos for:</p>
                    <ul className="list-disc pl-4 space-y-0.5">
                      {incompleteSteps
                        .filter(
                          (step) =>
                            !bulkFiles.some(
                              (f) => f.assignedStepKey === step.key
                            )
                        )
                        .map((step) => (
                          <li key={step.key}>{step.label}</li>
                        ))}
                    </ul>
                  </div>
                )}
              </main>
              <footer className="shrink-0 px-4 py-3 flex items-center gap-2 border-t bg-background">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setBulkFiles((prev) => {
                      prev.forEach((f) => URL.revokeObjectURL(f.previewUrl));
                      return [];
                    });
                    setPhase("camera");
                    startCamera();
                  }}
                >
                  Back
                </Button>
                <Button
                  className="ml-auto"
                  disabled={!allBulkAssigned}
                  onClick={commitBulkAssignment}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Continue
                </Button>
              </footer>
            </>
          )}

          {/* ── Confirm Phase ──────────────────────────── */}
          {phase === "confirm" && (
            <>
              <main className="flex-1 min-h-0 overflow-y-auto p-4">
                <ConfirmationList
                  steps={steps}
                  capturedFiles={capturedFiles}
                  alreadyDone={alreadyDone}
                  appointmentWorkflows={appointmentWorkflows}
                  onRetake={handleRetake}
                  onReplace={handleReplace}
                  submitting={submitting}
                />
              </main>
              <footer className="shrink-0 px-4 py-4 border-t bg-background space-y-2">
                {submitting && (
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
                <Button
                  className="w-full h-12 text-base bg-green-600 hover:bg-green-700 text-white"
                  disabled={!allStepsReady || submitting}
                  onClick={handleSubmit}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Uploading... {uploadProgress}%
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Submit
                    </>
                  )}
                </Button>
              </footer>
            </>
          )}
        </div>
      </div>

      {/* Hidden file inputs */}
      <input
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        ref={bulkInputRef}
        onChange={handleBulkFilesSelect}
      />
      <input
        type="file"
        accept="image/*"
        className="hidden"
        ref={replaceInputRef}
        onChange={handleReplaceFileSelect}
      />

      {/* Close confirmation dialog */}
      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave photo capture?</AlertDialogTitle>
            <AlertDialogDescription>
              You still have incomplete steps. Your captured photos will be lost if you leave now.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Capturing</AlertDialogCancel>
            <AlertDialogAction
              onClick={forceClose}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Leave Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>,
    document.body
  );
}
